/**
 * Konteks autentikasi.
 *
 * - Token akses disimpan di memori + sessionStorage (kunci 'yys_token').
 * - Saat mount: bila ada token, panggil GET /auth/me untuk memuat profil;
 *   bila gagal, bersihkan sesi (tanpa loop reload).
 * - Permission dicek PER KODE yang sama persis dengan backend
 *   (mis. 'invoice:read', 'journal:post') — lihat
 *   apps/api/src/security/permissions.ts.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { api, hapusToken, pasangPenanganTidakSah, pesanKesalahan, setToken, getToken } from './api'
import type { ProfilPengguna, SatuanAkses } from './types'

interface HasilLogin {
  accessToken: string
  expiresIn: number
  user: ProfilPengguna
}

export interface NilaiAuth {
  pengguna: ProfilPengguna | null
  izin: Set<string>
  unit: SatuanAkses[]
  siap: boolean
  memuat: boolean
  masuk: (username: string, password: string) => Promise<ProfilPengguna>
  keluar: () => Promise<void>
  segarkanProfil: () => Promise<ProfilPengguna | null>
  punyaIzin: (...kode: string[]) => boolean
  namaUnit: (unitId: string | null | undefined) => string
}

const KonteksAuth = createContext<NilaiAuth | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const [pengguna, setPengguna] = useState<ProfilPengguna | null>(null)
  const [siap, setSiap] = useState(false)
  const [memuat, setMemuat] = useState(false)
  const sudahDimuat = useRef(false)

  const bersihkanSesi = useCallback(() => {
    hapusToken()
    setPengguna(null)
  }, [])

  // Penangan 401 terpusat: hanya dipicu api.ts bila permintaan MEMBAWA token.
  useEffect(() => {
    pasangPenanganTidakSah(() => {
      setPengguna(null)
      navigate('/login', { replace: true })
    })
    return () => pasangPenanganTidakSah(null)
  }, [navigate])

  const segarkanProfil = useCallback(async (): Promise<ProfilPengguna | null> => {
    if (!getToken()) {
      setPengguna(null)
      return null
    }
    try {
      const profil = await api.get<ProfilPengguna>('/auth/me')
      setPengguna(profil)
      return profil
    } catch {
      bersihkanSesi()
      return null
    }
  }, [bersihkanSesi])

  useEffect(() => {
    if (sudahDimuat.current) return
    sudahDimuat.current = true
    void (async () => {
      if (getToken()) await segarkanProfil()
      else setPengguna(null)
      setSiap(true)
    })()
  }, [segarkanProfil])

  const masuk = useCallback(
    async (username: string, password: string): Promise<ProfilPengguna> => {
      setMemuat(true)
      try {
        const hasil = await api.post<HasilLogin>('/auth/login', { username, password })
        setToken(hasil.accessToken)
        setPengguna(hasil.user)
        return hasil.user
      } finally {
        setMemuat(false)
      }
    },
    [],
  )

  const keluar = useCallback(async (): Promise<void> => {
    try {
      await api.post('/auth/logout', {})
    } catch {
      /* logout lokal tetap dilanjutkan walau server menolak */
    } finally {
      bersihkanSesi()
      navigate('/login', { replace: true })
    }
  }, [bersihkanSesi, navigate])

  const izin = useMemo(() => new Set(pengguna?.permissions ?? []), [pengguna])
  const unit = useMemo(() => pengguna?.units ?? [], [pengguna])

  const punyaIzin = useCallback(
    (...kode: string[]): boolean => {
      if (kode.length === 0) return true
      return kode.some((k) => izin.has(k))
    },
    [izin],
  )

  const namaUnit = useCallback(
    (unitId: string | null | undefined): string => {
      if (!unitId) return '-'
      const u = unit.find((x) => x.unitId === unitId)
      return u ? u.name : unitId
    },
    [unit],
  )

  const nilai = useMemo<NilaiAuth>(
    () => ({
      pengguna,
      izin,
      unit,
      siap,
      memuat,
      masuk,
      keluar,
      segarkanProfil,
      punyaIzin,
      namaUnit,
    }),
    [pengguna, izin, unit, siap, memuat, masuk, keluar, segarkanProfil, punyaIzin, namaUnit],
  )

  return <KonteksAuth.Provider value={nilai}>{children}</KonteksAuth.Provider>
}

export function useAuth(): NilaiAuth {
  const ctx = useContext(KonteksAuth)
  if (!ctx) throw new Error('useAuth() harus dipakai di dalam <AuthProvider>.')
  return ctx
}

/** Cek satu atau beberapa permission (benar bila salah satu dimiliki). */
export function usePermission(...kode: string[]): boolean {
  const { izin } = useAuth()
  if (kode.length === 0) return true
  return kode.some((k) => izin.has(k))
}

/** Pesan kesalahan login yang ramah. */
export function pesanGagalMasuk(e: unknown): string {
  return pesanKesalahan(e)
}
